// Duplicating a design template. A template owns a screen's whole look and
// its copy, so "this screen needs different wording" means a template of its
// own — copying an existing one is the short path to that.

import type { Template, TemplateBody } from "@/api/types";

/** Every design field of a template as a create/patch body — null means
 *  "unset", which the wire expresses by omission. */
export function toTemplateBody(template: Template): TemplateBody {
    return {
        backgroundColor: template.backgroundColor,
        backgroundImageAssetId: template.backgroundImageAssetId ?? undefined,
        layout: template.layout,
        distribution: template.distribution,
        sections: template.sections,
        fontStack: template.fontStack ?? undefined,
        fontAssetId: template.fontAssetId ?? undefined,
        colorSlots: template.colorSlots,
        customCss: template.customCss ?? undefined,
        previewWidth: template.previewWidth ?? undefined,
        previewHeight: template.previewHeight ?? undefined,
        // The resolved URLs travel with the ids they belong to. They are pinned
        // to an asset generation, not to a template, so they are just as valid
        // for the copy — and leaving them behind would hand the duplicate an
        // asset reference no screen can render, since the display endpoint
        // cannot resolve one for itself. A section's resolved `assetUrl`
        // travels with its `assetId` for the same reason.
        backgroundImageUrl: template.backgroundImageUrl ?? undefined,
        fontFaceUrl: template.fontFaceUrl ?? undefined,
    };
}

/** Template names are unique per workspace, so a duplicate picks the first
 *  free "(copy)" suffix rather than letting the server 409 the obvious one. */
export function duplicateName(source: string, taken: Iterable<string>): string {
    const names = new Set(taken);
    const first = `${source} (copy)`;
    if (!names.has(first)) return first;
    for (let n = 2; n < 100; n++) {
        const candidate = `${source} (copy ${n})`;
        if (!names.has(candidate)) return candidate;
    }
    return first; // 99 copies deep: let the server's uniqueness check answer.
}
